/**
 * Schemas Zod transversales. Regla del skill modulo-canonico: los montos viajan SIEMPRE
 * como string decimal (Money.parse los valida); jamás z.number() para dinero.
 */
import { z } from 'zod';

/**
 * Con coma decimal (formato venezolano: "1.234,56", "855,66", "1,185") se quitan los puntos de miles y la coma pasa
 * a punto. Sin coma el texto se toma como canónico ("1234.56", "1.185"), así que las integraciones no cambian.
 */
export function comaDecimal(s: string): string {
  if (!s.includes(',')) return s;
  const partes = s.split(',');
  if (partes.length !== 2) return s;
  return `${partes[0]!.replace(/\./g, '')}.${partes[1]}`;
}

/** Decimal, hasta 8 decimales. Sale siempre canónico con punto: "1234.56". */
export const decimalString = z
  .string()
  .trim()
  .transform(comaDecimal)
  .pipe(z.string().regex(/^-?\d{1,16}(\.\d{1,8})?$/, 'Monto inválido: escríbelo como 1.234,56'));

/** Decimal no negativo. */
export const decimalNoNegativo = decimalString.refine((s) => !s.startsWith('-'), 'No puede ser negativo');

/** Decimal estrictamente positivo. */
export const decimalPositivo = decimalString.refine(
  (s) => !s.startsWith('-') && /[1-9]/.test(s),
  'Debe ser mayor que cero',
);

export const uuid = z.uuid('Identificador inválido');

export const paginacion = z.object({
  pagina: z.coerce.number().int().min(1).default(1),
  limite: z.coerce.number().int().min(1).max(100).default(25),
});
export type Paginacion = z.infer<typeof paginacion>;

export interface Pagina<T> {
  datos: T[];
  total: number;
  pagina: number;
  limite: number;
}

/** RIF/CI venezolano: V/E/J/G/P-########-# (guiones opcionales al entrar; se normaliza). */
export const rif = z
  .string()
  .trim()
  .toUpperCase()
  .transform((s) => s.replace(/[\s.]/g, ''))
  .pipe(z.string().regex(/^[VEJGPC]-?\d{6,9}-?\d$/, 'RIF/CI inválido: elige el prefijo y escribe de 7 a 10 dígitos sin guiones'))
  .transform((s) => {
    const limpio = s.replace(/-/g, '');
    return `${limpio[0]}-${limpio.slice(1, -1)}-${limpio.slice(-1)}`;
  });

/**
 * Identificación de un CLIENTE: RIF (con su dígito verificador) o cédula (sin él). Con V o E y hasta 8 dígitos es una
 * cédula y se guarda «V-12345678»; con 9 o 10 dígitos, o con J, G, P o C, es un RIF y se guarda «J-12345678-9». Antes
 * toda identificación se partía como RIF y la cédula 12.345.678 quedaba «V-1234567-8».
 */
export const identificacionCliente = z
  .string()
  .trim()
  .toUpperCase()
  .transform((s) => s.replace(/[\s.]/g, ''))
  .pipe(z.string().regex(/^[VEJGPC]-?\d{6,9}-?\d$/, 'RIF o cédula inválidos: elige la letra y escribe solo los números'))
  .transform((s) => {
    const limpio = s.replace(/-/g, '');
    const letra = limpio[0]!;
    const digitos = limpio.slice(1);
    return (letra === 'V' || letra === 'E') && digitos.length <= 8 ? `${letra}-${digitos}` : `${letra}-${digitos.slice(0, -1)}-${digitos.slice(-1)}`;
  });

/** La misma persona por cédula (V-12345678) y por RIF (V-12345678-9): el número sin el dígito final, o null si no aplica. */
export function cedulaDe(identificacion: string): string | null {
  const m = /^([VE])-(\d{6,8})(?:-\d)?$/.exec(identificacion.toUpperCase());
  return m ? `${m[1]}-${m[2]}` : null;
}

export const email = z.email('Correo inválido').trim().toLowerCase();

export const fechaISO = z.iso.date();
export const fechaHoraISO = z.iso.datetime({ offset: true });

/** Rango de fechas (inclusive) para reportes. */
export const rangoFechas = z
  .object({ desde: fechaISO, hasta: fechaISO })
  .refine((r) => r.desde <= r.hasta, 'El rango es inválido: "desde" es posterior a "hasta"');
